import React, { useEffect, useMemo, useState } from "react";
import { X, UserPlus } from "lucide-react";
import { warehouseService } from "../../../services/warehouse.service";


function SelectField({ placeholder = "Select", options = [], value, onChange, disabled }) {
  return (
    <div className="relative w-full">
      <select
        value={value || ""}
        onChange={(e) => onChange?.(e.target.value)}
        disabled={disabled}
        className={
          "h-[34px] w-full appearance-none rounded-[8px] border border-[#f2c6cc] bg-white px-4 pr-9 text-[12px] text-slate-600 outline-none " +
          "focus:border-[#d86a78] focus:ring-2 focus:ring-[#f7d7db] " +
          (disabled ? "opacity-60 pointer-events-none" : "")
        }
      >
        <option value="">{placeholder}</option>
        {options.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>

      <svg
        className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2"
        width="14"
        height="14"
        viewBox="0 0 24 24"
        fill="none"
      >
        <path
          d="M6 9l6 6 6-6"
          stroke="#d86a78"
          strokeWidth="2.4"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    </div>
  );
}

export default function AssignEmployeeDrawer({ open, onClose, warehouse }) {
  const [state, setState] = useState({ loading: false, error: "", employees: [] });
  const [saving, setSaving] = useState(false);
  const [selectedManagerId, setSelectedManagerId] = useState("");

  // lock body scroll when open
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [open]);

  // load employees when drawer opens
 useEffect(() => {
  if (!open) return;
  let alive = true;

  (async () => {
    try {
      setState({ loading: true, error: "", employees: [] });

      const res = await warehouseService.listWarehouseEmployeesLookup();

      const allEmployees = Array.isArray(res?.data)
        ? res.data
        : Array.isArray(res?.items)
        ? res.items
        : Array.isArray(res?.data?.data)
        ? res.data.data
        : Array.isArray(res?.rows)
        ? res.rows
        : [];

      const employees = allEmployees.map((e) => ({
        id: e?.id || e?.employeeId || e?.emp_id || "",
        name: e?.name || e?.fullName || e?.employeeName || "",
        email: e?.email || e?.employeeEmail || "",
      }));

      if (!alive) return;
      setState({ loading: false, error: "", employees });
    } catch (e) {
      if (!alive) return;
      setState({
        loading: false,
        error: e?.message || "Failed to load employees",
        employees: [],
      });
    }
  })();

  return () => {
    alive = false;
  };
}, [open]);

  // preset selected manager from warehouse
  useEffect(() => {
    if (!open) return;
    setSelectedManagerId(warehouse?._raw?.manager_emp_id || "");
  }, [open, warehouse]);

  const employeeOptions = useMemo(() => {
    return (state.employees || []).map((e) => ({
      value: e.id,
      label: `${e.name || e.id}${e.email ? ` (${e.email})` : ""}`,
    }));
  }, [state.employees]);

  async function assignManager(empId) {
    try {
      if (!warehouse?.id) return;
      setSaving(true);

      const emp = (state.employees || []).find((x) => String(x.id) === String(empId));
      await warehouseService.updateWarehouse(warehouse.id, {
        managerEmpId: emp?.id || empId,
        managerName: emp?.name,
        managerEmail: emp?.email,
      });
    } finally {
      setSaving(false);
    }
  }

  return (
    <>
      {/* overlay */}
      <div
        className={
          "fixed inset-0 z-[999999] transition-opacity duration-200 " +
          (open ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none")
        }
        aria-hidden={!open}
      >
        <div className="absolute inset-0 bg-black/20" onClick={onClose} />
      </div>

      {/* drawer */}
      <div
        className={
          "fixed right-0 top-0 z-[1000000] h-full w-[420px] max-w-[92vw] bg-white shadow-[0_18px_55px_rgba(17,24,39,0.22)] border-l border-slate-200/70 transition-transform duration-200 ease-out " +
          (open ? "translate-x-0" : "translate-x-full")
        }
        role="dialog"
        aria-modal="true"
        aria-label="Assign Employee"
      >
        <div className="flex h-full flex-col">
          {/* header */}
          <div className="px-6 pt-5">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <UserPlus size={18} className="text-[#d86a78]" />
                <div className="text-[16px] font-bold text-[#d86a78]">Assign Employee</div>
              </div>

              <button
                type="button"
                onClick={onClose}
                className="rounded-lg p-2 text-slate-500 hover:bg-slate-100"
                aria-label="Close"
              >
                <X size={18} />
              </button>
            </div>

            <div className="mt-4 h-[1px] w-full bg-[#f2c6cc]" />

            {warehouse?.name ? (
              <div className="mt-3 text-[12px] text-slate-500">
                <span className="font-semibold text-slate-600">Warehouse:</span> {warehouse.name}
              </div>
            ) : null}
          </div>

          {/* content */}
          <div className="flex-1 overflow-y-auto px-6 pb-6 pt-5">
            {state.error ? (
              <div className="mb-4 rounded-[10px] border border-rose-200 bg-rose-50 px-4 py-3 text-[12px] text-rose-700">
                {state.error}
              </div>
            ) : null}

            <div className="space-y-4">
              <div className="flex items-center gap-4">
                <div className="w-[160px] text-[12px] text-slate-500">Warehouse Manager</div>
                <div className="flex-1">
                  <SelectField
                    placeholder={state.loading ? "Loading..." : "Select"}
                    options={employeeOptions}
                    value={selectedManagerId}
                    disabled={state.loading || saving}
                    onChange={async (v) => {
                      setSelectedManagerId(v);
                      if (v) await assignManager(v);
                    }}
                  />
                </div>
              </div>

              {/* <div className="text-[11px] text-slate-400">
                Selecting a manager will update the warehouse manager in backend.
              </div> */}
            </div>
          </div>

          {/* footer */}
          <div className="px-6 pb-6">
            <button
              type="button"
              onClick={onClose}
              className="h-[40px] w-full rounded-[10px] bg-[#d86a78] text-[13px] font-semibold text-white hover:bg-[#c95b69] active:scale-[0.99] transition"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </>
  );
}
